import { LorebookVisibility, ModerationStatus } from "@next-platform/packages-ts-types";
import isLorebookStatusNeedReview from "./isLorebookStatusNeedReview";
/** Awaiting a verdict: the creator submitted, or an admin/report review is open. */
const AWAITING_REVIEW_STATUSES = new Set([ModerationStatus.Pending, ModerationStatus.Open, ModerationStatus.RereviewRequired]);
/**
 * Returns why a lorebook can't be attached to a chatbot/group, or `null` when it can. Mirrors the API's
 * attach guard (`AttachLorebookUseCase`) so the CTA never offers an attach the API would reject, and is
 * the single source of both the disabled state and the inline message on every attach surface:
 *
 *  - an open review cycle blocks only a lorebook that was never approved. A PUBLIC lorebook keeps
 *    serving its last approved version while an edit is re-reviewed, so it stays attachable;
 *  - flagged / rejected / needs-review entries block regardless of visibility, and so does a
 *    `needs_review` lorebook status.
 */
export const getLorebookAttachBlockedReason = (lorebook) => {
    if (!lorebook)
        return null;
    const moderationStatus = lorebook.moderationReportStatus;
    // A PUBLIC lorebook was approved at least once, so an open review never blocks it.
    if (moderationStatus && lorebook.visibility !== LorebookVisibility.PUBLIC) {
        if (AWAITING_REVIEW_STATUSES.has(moderationStatus)) {
            return "underReview";
        }
        if (moderationStatus === ModerationStatus.FixRequired) {
            return "actionRequired";
        }
    }
    if (typeof lorebook.num_flagged_entries === "number" && lorebook.num_flagged_entries > 0) {
        return "needsReview";
    }
    if (isLorebookStatusNeedReview(lorebook.status, lorebook.num_rejected_entries, lorebook.num_needs_review_entries)) {
        return "needsReview";
    }
    return null;
};
/** Convenience predicate over {@link getLorebookAttachBlockedReason} for surfaces that only gate a CTA. */
const isLorebookAttachBlocked = (lorebook) => getLorebookAttachBlockedReason(lorebook) !== null;
export default isLorebookAttachBlocked;
